/* Copyright (c) 2017 Backpack Health LLC */

import PropTypes from 'prop-types';
import * as _ from 'lodash';

import HealthEntryCategoryNames from '../../constants/HealthEntryCategoryNames';

import amount, { cleanAmount } from './amount';
import flag from './flag';
import current from './current';
import period from './period';
import reaction from './reaction';
import anatomy from './anatomy';
import values from './values';
import diagnosis from './diagnosis';
import geneVariantResult, { cleanGeneVariantResult } from './geneVariantResult';
import description, { cleanDescription } from './description';
import singleAnatomy, { cleanSingleAnatomy } from './singleAnatomy';
import stoppingReason, { cleanStoppingReason } from './stoppingReason';
import frequency, { cleanFrequency } from './frequency';
import medliTerm, { otherMedliTerm, subType, cleanMedliTermWithSubType } from './medliTerm';
import { startDate, endDate, date, cleanStartDate, cleanEndDate, cleanValueDate } from './dates';

import { healthEntryPrototype, singleOccurrencePrototype, multiOccurrencePrototype, heValuePrototype } from './healthEntryPrototypes';

function composeHealthEntry(category, occurenceProtoTypes, occurrenceAttributes, categoryAttributes, originalState, languageCode) {
  const context = {};
  const healthEntryState = _.cloneDeep(originalState);

  return _.assign(context,
    ...Object.keys(occurenceProtoTypes).map(k => occurenceProtoTypes[k].bind(context)()), {
      id: healthEntryState.id,
      category,
      data: () => healthEntryState,
    },
    ...Object.keys(occurrenceAttributes).map(k => ({
      [k]: occurrenceAttributes[k].bind(context)(healthEntryState, languageCode),
    })),
    ...Object.keys(categoryAttributes).map(k => ({
      [k]: categoryAttributes[k].bind(context)(healthEntryState[category], languageCode),
    }))
  );
}

function composeHeOccurrence(occurenceProtoTypes, occurrenceAttributes) {
  return (category, defaultData, categoryAttributes) => {
    return (initialData = {}, languageCode) => {
      const healthEntryState = _.assign({
        flagged: false,
        category,
        comments: [],
        [category]: _.assign({}, defaultData, _.cloneDeep(initialData[category])),
      }, initialData);

      return composeHealthEntry(category, occurenceProtoTypes, occurrenceAttributes, categoryAttributes, healthEntryState, languageCode);
    };
  };
}

function composeHeValue(occurenceProtoTypes, occurrenceAttributes) {
  return (category, defaultData, categoryAttributes) => {
    return (initialData = {}, languageCode) => {
      const healthEntryState = _.assign({
        flagged: false,
        comments: [],
      }, defaultData, initialData);
      const attributes = _.assign({}, occurrenceAttributes, categoryAttributes);
      return composeHealthEntry(category, occurenceProtoTypes, attributes, {}, healthEntryState, languageCode);
    };
  };
}

const createSingleOccurrence = composeHeOccurrence({ healthEntryPrototype, singleOccurrencePrototype }, { flag });
const createMultiOccurrence = composeHeOccurrence({ healthEntryPrototype, multiOccurrencePrototype }, {});
const createHealthEntryValue = composeHeValue({ heValuePrototype }, { flag });

const labValue = createHealthEntryValue(
  HealthEntryCategoryNames.lab, {}, { date, amount, description }
);

const measurementValue = createHealthEntryValue(
  HealthEntryCategoryNames.measurement, {}, { date, amount, description }
);

const geneticResultValue = createHealthEntryValue(
  HealthEntryCategoryNames.geneVariantResult, {}, { date, geneVariantResult }
);

const missingValue = createHealthEntryValue(
  'missingValue', {}, { date, amount, description }
);

const missing = createSingleOccurrence(
  'missing', {}, { current, period, medliTerm, anatomy, startDate, endDate }
);

const condition = createSingleOccurrence(
  HealthEntryCategoryNames.condition, {
    current: true,
  }, { current, period, medliTerm, anatomy, startDate, endDate, diagnosis }
);

const procedure = createSingleOccurrence(
  HealthEntryCategoryNames.procedure, {
    current: false,
    period: false,
  }, { period, medliTerm, anatomy, startDate, endDate }
);

const medication = createSingleOccurrence(
  HealthEntryCategoryNames.medication, {
    current: true,
  }, { current, period, medliTerm, amount, frequency, reaction, stoppingReason, startDate, endDate }
);

const otherTreatment = createSingleOccurrence(
  HealthEntryCategoryNames.otherTreatment, {
    current: true,
  }, { current, period, medliTerm, anatomy, frequency, reaction, stoppingReason, startDate, endDate }
);

const allergy = createSingleOccurrence(
  HealthEntryCategoryNames.allergy, {
    current: true,
  }, { current, period, medliTerm, otherMedliTerm, subType, reaction, startDate, endDate, diagnosis }
);

const vaccination = createSingleOccurrence(
  HealthEntryCategoryNames.vaccination, {
    current: false,
    period: false,
  }, { period, medliTerm, startDate, endDate }
);

const lab = createMultiOccurrence(
  HealthEntryCategoryNames.lab, {
    current: false,
  }, { medliTerm, values: values(labValue) }
);

const measurement = createMultiOccurrence(
  HealthEntryCategoryNames.measurement, {
    current: false,
  }, { medliTerm, anatomy: singleAnatomy, values: values(measurementValue) }
);

const gene = createMultiOccurrence(
  HealthEntryCategoryNames.geneticResult, {
    current: false,
  }, { medliTerm, values: values(geneticResultValue) }
);

const healthEntryData = {
  missing: {
    create: missing,
    cleaners: [],
  },
  [HealthEntryCategoryNames.condition]: {
    create: condition,
    cleaners: [cleanStartDate, cleanEndDate],
  },
  [HealthEntryCategoryNames.procedure]: {
    create: procedure,
    cleaners: [cleanStartDate, cleanEndDate],
  },
  [HealthEntryCategoryNames.medication]: {
    create: medication,
    cleaners: [cleanStartDate, cleanEndDate, cleanStoppingReason, cleanAmount, cleanFrequency],
  },
  [HealthEntryCategoryNames.otherTreatment]: {
    create: otherTreatment,
    cleaners: [cleanStartDate, cleanEndDate, cleanStoppingReason, cleanFrequency],
  },
  [HealthEntryCategoryNames.allergy]: {
    create: allergy,
    cleaners: [cleanStartDate, cleanEndDate, cleanMedliTermWithSubType],
  },
  [HealthEntryCategoryNames.vaccination]: {
    create: vaccination,
    cleaners: [cleanStartDate, cleanEndDate],
  },
  [HealthEntryCategoryNames.lab]: {
    create: lab,
    cleaners: [],
  },
  [HealthEntryCategoryNames.measurement]: {
    create: measurement,
    cleaners: [cleanSingleAnatomy],
  },
  [HealthEntryCategoryNames.geneticResult]: {
    create: gene,
    cleaners: [],
  },
  labValue: {
    create: labValue,
    cleaners: [],
  },
  measurementValue: {
    create: measurementValue,
    cleaners: [],
  },
  genetic_resultValue: {
    create: geneticResultValue,
    cleaners: [],
  },
  missingValue: {
    create: missingValue,
    cleaners: [],
  },
};

function composeHealthEntrySubmitCleaner(cleanerCollection) {
  return (initialData) => {
    const { category } = initialData;
    const healthEntryState = _.cloneDeep(initialData);
    let categoryData = healthEntryState[category];

    cleanerCollection.forEach((clean) => {
      categoryData = clean(categoryData);
    });
    healthEntryState[category] = categoryData;

    return healthEntryState;
  };
}

function composeHealthEntryValueSubmitCleaner(cleanerCollection) {
  return (initialData) => {
    let valueData = _.cloneDeep(initialData);
    cleanerCollection.forEach((clean) => {
      valueData = clean(valueData);
    });
    return valueData;
  };
}

function generalHealthEntryValue(category, data, languageCode) {
  const valueCategory = healthEntryData[`${category}Value`] ? `${category}Value` : 'missingValue';
  return healthEntryData[valueCategory].create(data, languageCode);
}

function generalHealthEntry(data, languageCode) {
  const category = (data.category && healthEntryData[data.category]) ? data.category : 'missing';
  return healthEntryData[category].create(data, languageCode);
}

function generalHealthEntryCleaner(data) {
  const category = (data.category && healthEntryData[data.category]) ? data.category : 'missing';
  const cleaner = composeHealthEntrySubmitCleaner(healthEntryData[category].cleaners);
  return cleaner(data);
}

function generalHealthEntryValueCleaner(value) {
  const cleaner = composeHealthEntryValueSubmitCleaner([cleanValueDate, cleanAmount, cleanDescription, cleanGeneVariantResult]);
  return cleaner(value);
}

export const cleaners = {
  condition: composeHealthEntrySubmitCleaner(healthEntryData[HealthEntryCategoryNames.condition].cleaners),
  procedure: composeHealthEntrySubmitCleaner(healthEntryData[HealthEntryCategoryNames.procedure].cleaners),
  medication: composeHealthEntrySubmitCleaner(healthEntryData[HealthEntryCategoryNames.medication].cleaners),
  otherTreatment: composeHealthEntrySubmitCleaner(healthEntryData[HealthEntryCategoryNames.otherTreatment].cleaners),
  allergy: composeHealthEntrySubmitCleaner(healthEntryData[HealthEntryCategoryNames.allergy].cleaners),
  vaccination: composeHealthEntrySubmitCleaner(healthEntryData[HealthEntryCategoryNames.vaccination].cleaners),
  lab: composeHealthEntrySubmitCleaner(healthEntryData[HealthEntryCategoryNames.lab].cleaners),
  measurement: composeHealthEntrySubmitCleaner(healthEntryData[HealthEntryCategoryNames.measurement].cleaners),
  geneticResult: composeHealthEntrySubmitCleaner(healthEntryData[HealthEntryCategoryNames.geneticResult].cleaners),

  generalHealthEntry: generalHealthEntryCleaner,
  generalHealthEntryValue: generalHealthEntryValueCleaner,
};

export const healthEntryPropTypes = {
  condition: PropTypes.object,
  procedure: PropTypes.object,
  allergy: PropTypes.object,
  vaccination: PropTypes.object,
  lab: PropTypes.object,
  measurement: PropTypes.object,
  medication: PropTypes.object,
  otherTreatment: PropTypes.object,
  general: PropTypes.shape({
    flagged: PropTypes.bool,
    category: PropTypes.string,
  }),
};

export {
  generalHealthEntry,
  generalHealthEntryValue,
};
